import React, { useEffect, useState } from 'react'
import { Products } from 'stylewhere/api'
import { AppStore } from 'stylewhere/shared'
import { Image } from './Image'
import { Spinner } from '@chakra-ui/react'
import { Box } from 'stylewhere/components'
import styled from '@emotion/styled'

interface Props {
  product?: any
  type?: string
  width?: number | string
  height?: number | string
  style?: React.CSSProperties
  variant?: 'boxed'
  transparent?: boolean
  maxHeightContainer?: number
  alt?: string
  'data-testid'?: string
  onEmptyImageComponent?: React.ReactNode
}

type ImageState = { status: 'loading' } | { status: 'ready'; src: string } | { status: 'error' }

export const PrivateImage = ({ product, type = 'medium', ...imageProps }: Props) => {
  const [state, setState] = useState<ImageState>({ status: 'loading' })

  useEffect(() => {
    if (!product || !AppStore.authToken) {
      setState({ status: 'error' })
      return
    }

    let objectUrl: string | null = null
    let cancelled = false

    const settings = AppStore.getShowProductImageSettings()
    const isPrivate = settings === 'StylewherePrivateUrl' || settings.toLowerCase() === 'yes'
    let publicUrl
    if (isPrivate) {
      const imageId = Products.getImageID(product)
      if (imageId && imageId !== '' && !Products.isProductImage(imageId)) {
        Products.fetchPrivateImage(imageId, type).then((url) => {
          if (!cancelled) {
            if (url) {
              objectUrl = url
              setState({ status: 'ready', src: url })
            } else {
              setState({ status: 'error' })
            }
          }
        })
      } else {
        publicUrl = Products.parseImageUrl(imageId, type)
        setState(publicUrl ? { status: 'ready', src: publicUrl } : { status: 'error' })
      }
    } else {
      publicUrl = Products.getPublicImageUrl(product, type)
      setState(publicUrl ? { status: 'ready', src: publicUrl } : { status: 'error' })
    }

    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [product, type])

  if (state.status === 'loading') {
    return (
      <Loader
        center
        style={{
          height: imageProps.height ?? 100,
          minHeight: imageProps.height ?? 100,
          maxHeight: imageProps.height ?? 100,
          maxWidth: imageProps.width ?? 100,
          width: imageProps.width ?? 100,
          minWidth: imageProps.width ?? 100,
          ...imageProps.style
        }}
      >
        <Spinner color="#e0e0e0" size="xl" />
      </Loader>
    )
  }

  if (state.status === 'error') {
    return <Image src={undefined} {...imageProps} />
  }

  // key forces Image to remount with the correct initial src
  // (Image captures src in useState on mount, so remount is needed when src changes)
  return <Image key={state.src} src={state.src} {...imageProps} />
}

const Loader = styled(Box)<{ transparent?: boolean }>`
  font-weight: 800;
  font-size: 10pt;
  text-align: center;
  color: rgba(0, 0, 0, 0.1);
  background-color: transparent;
  border-radius: 10px;
  margin: auto;
`
